import type { FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';

import { Analytics } from '@trello/atlassian-analytics';
import { forTemplate } from '@trello/legacy-i18n';
import { MemberAvatarUnconnected } from '@trello/member-avatar';
import type { PIIString } from '@trello/privacy';
import { convertToPIIString } from '@trello/privacy';

import { ViewsAndCopies } from 'app/src/components/BoardMenuViewsAndCopies';
import { TemplateShareButton } from './TemplateShareButton';
import { UseTemplateButton } from './UseTemplateButton';

import styles from './TemplateStoryHeader.less';

// only use for strings coming from admin.trello.com/admin/templates
const dangerousFormat = forTemplate('templates', {
  shouldEscapeStrings: false,
});

export const TemplateStoryHeader: FunctionComponent<{
  avatarShape: string;
  avatarUrl: string;
  boardId: string | null;
  byline: string;
  copyCount: number;
  shortLink: string;
  templateName: string;
  shouldShowBCUpgrade?: boolean;
  // if username is undefined we render the logged out experience
  username?: PIIString;
  storyPageUrl: string;
  viewCount: number;
}> = ({
  avatarShape,
  avatarUrl,
  boardId,
  byline,
  copyCount,
  templateName,
  shortLink,
  shouldShowBCUpgrade,
  username,
  storyPageUrl,
  viewCount,
}) => {
  const getImageUrl = () => ({
    '30': `${avatarUrl}/30.png`,
    '50': `${avatarUrl}/50.png`,
    '170': `${avatarUrl}/170.png`,
  });

  const trackBcUpgradeLinkClick = () => {
    Analytics.sendClickedLinkEvent({
      linkName: 'bcUpgradeFromTemplatesStoryHeaderLink',
      source: 'templateStoryPageScreen',
    });
  };

  return (
    <div className={styles.header}>
      <div className={styles.templateHeaderInfo}>
        <div className={styles.storyPageAvatar}>
          <MemberAvatarUnconnected
            avatars={getImageUrl()}
            avatarClassName={classNames(styles.storyPageAvatar, {
              [styles.orgAvatar]: avatarShape === 'square',
            })}
            fullName={convertToPIIString(byline)}
            size={64}
          />
        </div>
        <div className={styles.headerText}>
          <h1 className={styles.templateName}>{templateName}</h1>
          <div className={styles.creator}>
            {dangerousFormat('created-by', { creator: byline })}
          </div>
          <div className={styles.metadata}>
            <ViewsAndCopies
              isTemplate={true}
              showLabels={true}
              useSmallTextStyle={false}
              copyCount={copyCount}
              viewCount={viewCount}
            />
            {shouldShowBCUpgrade && (
              <>
                <span
                  className={classNames(
                    'icon-sm',
                    'icon-business-class',
                    'icon-business-class-color',
                  )}
                ></span>
                <FormattedMessage
                  id="templates.templates.works-best-with-business-class"
                  defaultMessage="Works best with {businessClassLink}"
                  description="An upsell for upgrading your subscription"
                  values={{
                    businessClassLink: (
                      <a
                        href="/business-class"
                        target="_blank"
                        onClick={trackBcUpgradeLinkClick}
                        key={0}
                      >
                        <FormattedMessage
                          id="templates.templates.business-class"
                          defaultMessage="Premium"
                          description="Premium"
                        />
                      </a>
                    ),
                  }}
                />
              </>
            )}
          </div>
        </div>
      </div>

      <div className={styles.templateLink}>
        {shortLink && (
          <>
            <TemplateShareButton
              storyPageUrl={storyPageUrl}
              username={username}
              boardId={boardId || ''}
              name={templateName}
            />
          </>
        )}

        {boardId && <UseTemplateButton boardId={boardId} username={username} />}
      </div>
    </div>
  );
};
